import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys, useApiMutation, useApiQuery } from '../api/hooks';
import type {
  BranchAvailabilityRequest,
  BranchResponse,
  ChangeServiceStatusRequest,
  CreateAreaRequest,
  CreateServiceRequest,
  EligibleDoctorsRequest,
  PricingMode,
  RoomType,
  SeedResultResponse,
  ServiceAreaResponse,
  ServiceCategory,
  ServiceResponse,
  ServiceStatus,
  StaffMemberResponse,
  UpdateAreaRequest,
  UpdateServiceRequest,
  VatClass,
} from '../api/types';
import { AsyncSection } from '../components/AsyncSection';
import { PageHeader } from '../components/PageHeader';
import { ErrorState } from '../components/states/StateViews';
import { useAuthStore } from '../auth/authStore';
import { BilingualNameFields, Field, MoneyField } from './catalog/fields';
import { money, readMoney } from './catalog/money';
import { useLocalisedName } from '../i18n/localisedName';

const CATEGORIES: ServiceCategory[] = ['LASER', 'DERMA', 'FACIAL', 'BODY'];
const VAT_CLASSES: VatClass[] = ['COSMETIC', 'THERAPEUTIC'];
const PRICING_MODES: PricingMode[] = ['AREA', 'PULSE'];

/**
 * The room types a service can ask for (CR-21). "TREATMENT", the old shared type, is left out: it
 * is shown only on a service that still has it, so the clinic can move it to the right one.
 */
const ROOM_TYPES: RoomType[] = ['LASER', 'DERMATOLOGY', 'FACIAL', 'BODY', 'CONSULTATION'];

/** The room a new service needs, suggested from its category: laser in laser rooms, and so on. */
const ROOM_FOR_CATEGORY: Record<ServiceCategory, RoomType> = {
  LASER: 'LASER',
  DERMA: 'DERMATOLOGY',
  FACIAL: 'FACIAL',
  BODY: 'BODY',
};

/** What a session costs, as the list shows it: a price, a price per pulse, or "not priced". */
function usePriceLabel() {
  const { t } = useTranslation();
  return (service: ServiceResponse): string => {
    if (service.pricingMode === 'PULSE') {
      const perPulse = money(service.pulsePrice);
      return perPulse ? t('catalog.perPulse', { price: perPulse }) : t('catalog.unpriced');
    }
    return money(service.basePrice) ?? t('catalog.unpriced');
  };
}

/** Whether the service has the price it is charged at, which is what going on sale needs. */
function isPriced(service: ServiceResponse): boolean {
  return service.pricingMode === 'PULSE' ? Boolean(service.pulsePrice) : Boolean(service.basePrice);
}

/**
 * The clinic's service list (FR-201, FR-202).
 *
 * Two things on this screen are worth more than they look. The starter pack turns an afternoon of
 * typing body-area names in two languages into one button, which is most of what makes assisted
 * onboarding a single visit. And a service cannot be put on sale without a price — the screen says
 * so before the save is attempted rather than after, because "why was that rejected" is a worse
 * experience than "you need this first".
 */
export function CatalogPage() {
  const localised = useLocalisedName();
  const { t } = useTranslation();
  const priceLabel = usePriceLabel();
  const clinicId = useAuthStore((state) => state.activeClinicId);
  const permissions = useAuthStore((state) => state.permissions);
  const canManage = permissions.has('CATALOG_MANAGE');
  const canSeeStaff = permissions.has('STAFF_READ');

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const services = useApiQuery<ServiceResponse[]>(
    queryKeys.services(clinicId ?? ''),
    `/clinics/${clinicId}/catalog/services`,
    { enabled: Boolean(clinicId) },
  );

  const createService = useApiMutation<ServiceResponse, CreateServiceRequest>(
    (body) => ({ path: `/clinics/${clinicId}/catalog/services`, options: { method: 'POST', body } }),
    { invalidates: [queryKeys.services(clinicId ?? '')], onSuccess: () => setCreating(false) },
  );

  const applySeedPack = useApiMutation<SeedResultResponse, void>(
    () => ({ path: `/clinics/${clinicId}/catalog/seed-pack`, options: { method: 'POST' } }),
    { invalidates: [queryKeys.services(clinicId ?? '')] },
  );

  if (!clinicId) {
    return <PageHeader title={t('nav.items.catalog')} description={t('home.noClinic')} />;
  }

  return (
    <>
      <PageHeader
        title={t('nav.items.catalog')}
        description={t('catalog.description')}
        actions={
          canManage ? (
            <button type="button" className="button button--primary" onClick={() => setCreating((open) => !open)}>
              {creating ? t('common.cancel') : t('catalog.add')}
            </button>
          ) : null
        }
      />

      {creating ? (
        <ServiceForm
          onSubmit={(body) => createService.mutate(body)}
          pending={createService.isPending}
          error={createService.error}
        />
      ) : null}

      {/*
        Outside the section below rather than inside it: an empty catalogue renders the empty
        state, and the starter pack is the answer to being empty — offering it only once the
        clinic already has services would be offering it to nobody.
      */}
      {canManage && services.data?.length === 0 ? (
        <div className="panel" style={{ marginBlockEnd: 'var(--space-5)' }}>
          <h2 className="section__title">{t('catalog.seed.title')}</h2>
          <p className="field__hint">{t('catalog.seed.description')}</p>
          <button
            type="button"
            className="button button--primary"
            style={{ marginBlockStart: 'var(--space-3)' }}
            disabled={applySeedPack.isPending}
            onClick={() => applySeedPack.mutate()}
          >
            {applySeedPack.isPending ? t('catalog.saving') : t('catalog.seed.apply')}
          </button>
          {applySeedPack.data ? (
            <p className="field__hint">
              {t('catalog.seed.done', {
                services: applySeedPack.data.servicesCreated ?? 0,
                areas: applySeedPack.data.areasCreated ?? 0,
              })}
            </p>
          ) : null}
          {applySeedPack.error ? <ErrorState error={applySeedPack.error} /> : null}
        </div>
      ) : null}

      <AsyncSection
        query={services}
        skeletonRows={6}
        isEmpty={(rows) => rows.length === 0}
        emptyTitle={t('catalog.emptyTitle')}
        emptyDescription={t('catalog.emptyDescription')}
      >
        {(rows) => (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">{t('catalog.code')}</th>
                    <th scope="col">{t('catalog.name')}</th>
                    <th scope="col">{t('catalog.category')}</th>
                    <th scope="col">{t('catalog.vatClass')}</th>
                    <th scope="col">{t('catalog.duration')}</th>
                    <th scope="col">{t('catalog.roomType')}</th>
                    <th scope="col">{t('catalog.price')}</th>
                    <th scope="col">{t('catalog.areas')}</th>
                    <th scope="col">{t('common.status')}</th>
                    <th scope="col">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((service) => (
                    <tr key={service.id}>
                      <th scope="row" className="ltr">
                        {service.code}
                      </th>
                      <td>{localised.name(service)}</td>
                      <td>{t(`catalog.categories.${service.category ?? 'LASER'}`)}</td>
                      <td>{t(`catalog.vatClasses.${service.vatClass ?? 'COSMETIC'}`)}</td>
                      <td className="numeric">{t('catalog.minutes', { n: service.durationMinutes ?? 0 })}</td>
                      <td>
                        {service.requiredRoomType
                          ? t(`branches.roomTypes.${service.requiredRoomType}`)
                          : t('catalog.anyRoom')}
                      </td>
                      <td className="numeric">{priceLabel(service)}</td>
                      <td className="numeric">{service.areaCount ?? 0}</td>
                      <td>
                        <span className={service.status === 'ACTIVE' ? 'badge' : 'badge badge--muted'}>
                          {t(`catalog.statuses.${service.status ?? 'DRAFT'}`)}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="button button--ghost"
                          onClick={() =>
                            setSelectedId((current) => (current === service.id ? null : (service.id ?? null)))
                          }
                        >
                          {selectedId === service.id ? t('catalog.hideSetup') : t('catalog.setUp')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {selectedId ? (
              <ServiceSetup
                clinicId={clinicId}
                service={rows.find((row) => row.id === selectedId) ?? null}
                canManage={canManage}
                canSeeStaff={canSeeStaff}
              />
            ) : null}
          </>
        )}
      </AsyncSection>
    </>
  );
}

// ── Create a service ────────────────────────────────────────────────────────

function ServiceForm({
  onSubmit,
  pending,
  error,
}: {
  readonly onSubmit: (body: CreateServiceRequest) => void;
  readonly pending: boolean;
  readonly error: unknown;
}) {
  const { t } = useTranslation();

  return (
    <form
      className="panel"
      style={{ marginBlockEnd: 'var(--space-5)' }}
      onSubmit={(event) => {
        event.preventDefault();
        const form = new FormData(event.currentTarget);
        onSubmit({
          code: String(form.get('code') ?? '').trim(),
          ...readServiceFields(form),
          displayOrder: 0,
        });
      }}
    >
      <div className="form-grid">
        <Field name="code" label={t('catalog.code')} required hint={t('catalog.codeHint')} dir="ltr" />
        <ServiceFields />
      </div>

      {error ? <ErrorState error={error} /> : null}

      <div style={{ marginBlockStart: 'var(--space-4)' }}>
        <button type="submit" className="button button--primary" disabled={pending}>
          {pending ? t('catalog.saving') : t('catalog.create')}
        </button>
        <p className="field__hint">{t('catalog.createHint')}</p>
      </div>
    </form>
  );
}

// ── The fields a service is described by, shared by "add" and "edit" ───────

/**
 * Names, category, VAT, length, room, pricing and machine (FR-201, CR-21).
 *
 * The room is suggested from the category until somebody picks one, because "laser in a laser room,
 * dermatology in a dermatology room" is right far more often than not. Pricing by pulse is offered
 * only for laser, and only the price that will actually be charged is asked for: the base price when
 * priced by area, the price per pulse when priced by pulse. The other one is kept as it was, so
 * switching back and forth loses nothing.
 */
function ServiceFields({ initial }: { readonly initial?: ServiceResponse }) {
  const { t } = useTranslation();
  const [category, setCategory] = useState<ServiceCategory>(initial?.category ?? 'LASER');
  const [roomType, setRoomType] = useState<RoomType | ''>(
    initial ? (initial.requiredRoomType ?? '') : ROOM_FOR_CATEGORY.LASER,
  );
  const [roomChosen, setRoomChosen] = useState(Boolean(initial));
  const [pricingMode, setPricingMode] = useState<PricingMode>(initial?.pricingMode ?? 'AREA');

  const laser = category === 'LASER';
  const mode: PricingMode = laser ? pricingMode : 'AREA';
  // The old shared type stays selectable on a service that still has it, and nowhere else.
  const roomOptions: RoomType[] = roomType === 'TREATMENT' ? [...ROOM_TYPES, 'TREATMENT'] : ROOM_TYPES;

  return (
    <>
      <BilingualNameFields arDefault={initial?.nameAr} enDefault={initial?.nameEn} />
      <label className="field">
        <span className="field__label">{t('catalog.category')}</span>
        <select
          name="category"
          className="field__input"
          value={category}
          onChange={(event) => {
            const next = event.target.value as ServiceCategory;
            setCategory(next);
            if (!roomChosen) {
              setRoomType(ROOM_FOR_CATEGORY[next]);
            }
          }}
        >
          {CATEGORIES.map((option) => (
            <option key={option} value={option}>
              {t(`catalog.categories.${option}`)}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span className="field__label">{t('catalog.vatClass')}</span>
        <select name="vatClass" className="field__input" defaultValue={initial?.vatClass ?? 'COSMETIC'}>
          {VAT_CLASSES.map((vatClass) => (
            <option key={vatClass} value={vatClass}>
              {t(`catalog.vatClasses.${vatClass}`)}
            </option>
          ))}
        </select>
        <span className="field__hint">{t('catalog.vatHint')}</span>
      </label>
      <label className="field">
        <span className="field__label">{t('catalog.duration')}</span>
        <input
          type="number"
          name="durationMinutes"
          className="field__input numeric"
          min={5}
          step={5}
          defaultValue={initial?.durationMinutes ?? 30}
          required
        />
      </label>
      <label className="field">
        <span className="field__label">{t('catalog.roomType')}</span>
        <select
          name="requiredRoomType"
          className="field__input"
          value={roomType}
          onChange={(event) => {
            setRoomType(event.target.value as RoomType | '');
            setRoomChosen(true);
          }}
        >
          <option value="">{t('catalog.anyRoom')}</option>
          {roomOptions.map((option) => (
            <option key={option} value={option}>
              {t(`branches.roomTypes.${option}`)}
            </option>
          ))}
        </select>
        <span className="field__hint">
          {roomType === 'TREATMENT' ? t('catalog.roomTypeOld') : t('catalog.roomTypeHint')}
        </span>
      </label>
      {laser ? (
        <label className="field">
          <span className="field__label">{t('catalog.pricingMode')}</span>
          <select
            name="pricingMode"
            className="field__input"
            value={pricingMode}
            onChange={(event) => setPricingMode(event.target.value as PricingMode)}
          >
            {PRICING_MODES.map((option) => (
              <option key={option} value={option}>
                {t(`catalog.pricingModes.${option}`)}
              </option>
            ))}
          </select>
          <span className="field__hint">{t('catalog.pricingHint')}</span>
        </label>
      ) : null}
      {mode === 'PULSE' ? (
        <MoneyField
          name="pulsePrice"
          label={t('catalog.pulsePrice')}
          hint={t('catalog.priceHint')}
          defaultValue={initial?.pulsePrice?.amount}
        />
      ) : (
        <MoneyField
          name="basePrice"
          label={t('catalog.price')}
          hint={t('catalog.priceHint')}
          defaultValue={initial?.basePrice?.amount}
        />
      )}
      <Field
        name="requiredMachineType"
        label={t('catalog.machineType')}
        hint={t('catalog.machineTypeHint')}
        defaultValue={initial?.requiredMachineType}
      />
    </>
  );
}

/**
 * Reads {@link ServiceFields} back. A price field that is not on screen keeps its old value rather
 * than clearing it.
 */
function readServiceFields(form: FormData, initial?: ServiceResponse) {
  const category = String(form.get('category') ?? 'LASER') as ServiceCategory;
  const chosenMode = (form.get('pricingMode') as PricingMode | null) ?? 'AREA';
  const pricingMode: PricingMode = category === 'LASER' ? chosenMode : 'AREA';
  return {
    nameAr: String(form.get('nameAr') ?? '').trim() || undefined,
    nameEn: String(form.get('nameEn') ?? '').trim() || undefined,
    category,
    vatClass: String(form.get('vatClass') ?? 'COSMETIC') as VatClass,
    durationMinutes: Number(form.get('durationMinutes') ?? 30),
    pricingMode,
    basePrice: form.has('basePrice') ? readMoney(form.get('basePrice')) : initial?.basePrice,
    pulsePrice: form.has('pulsePrice') ? readMoney(form.get('pulsePrice')) : initial?.pulsePrice,
    requiredRoomType: (String(form.get('requiredRoomType') ?? '') || undefined) as RoomType | undefined,
    requiredMachineType: String(form.get('requiredMachineType') ?? '').trim() || undefined,
  };
}

// ── One service: details, status, doctors, body areas, branch availability ──

function ServiceSetup({
  clinicId,
  service,
  canManage,
  canSeeStaff,
}: {
  readonly clinicId: string;
  readonly service: ServiceResponse | null;
  readonly canManage: boolean;
  readonly canSeeStaff: boolean;
}) {
  if (!service?.id) {
    return null;
  }

  return (
    <>
      {canManage ? <DetailsSection key={service.id} clinicId={clinicId} service={service} /> : null}
      <StatusSection clinicId={clinicId} service={service} canManage={canManage} />
      {canSeeStaff ? (
        <DoctorsSection key={`doctors-${service.id}`} clinicId={clinicId} service={service} canManage={canManage} />
      ) : null}
      <AreasSection
        clinicId={clinicId}
        serviceId={service.id}
        pricedByPulse={service.pricingMode === 'PULSE'}
        canManage={canManage}
      />
      <BranchAvailabilitySection clinicId={clinicId} service={service} canManage={canManage} />
    </>
  );
}

/**
 * Edits what the service is (CR-21): until now a service could only be created, so a wrong room,
 * price or length was there for good. The API replaces the details as a whole, so what this form
 * does not show (descriptions, sort order) is sent back unchanged.
 */
function DetailsSection({ clinicId, service }: { readonly clinicId: string; readonly service: ServiceResponse }) {
  const { t } = useTranslation();

  const save = useApiMutation<ServiceResponse, UpdateServiceRequest>(
    (body) => ({
      path: `/clinics/${clinicId}/catalog/services/${service.id}`,
      options: { method: 'PATCH', body },
    }),
    // Offerings are cached per branch; the prefix clears every branch's copy.
    { invalidates: [queryKeys.services(clinicId), ['clinics', clinicId, 'catalog', 'offerings']] },
  );

  return (
    <section className="section" aria-labelledby="service-details-heading">
      <h2 className="section__title" id="service-details-heading">
        {t('catalog.detailsTitle')}
      </h2>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          save.mutate({
            ...readServiceFields(form, service),
            descriptionAr: service.descriptionAr,
            descriptionEn: service.descriptionEn,
            displayOrder: service.displayOrder ?? 0,
          });
        }}
      >
        <div className="form-grid">
          <ServiceFields initial={service} />
        </div>
        {save.error ? <ErrorState error={save.error} /> : null}
        <div className="toolbar" style={{ marginBlockStart: 'var(--space-3)' }}>
          <button type="submit" className="button button--primary" disabled={save.isPending}>
            {save.isPending ? t('catalog.saving') : t('catalog.saveDetails')}
          </button>
          {save.isSuccess && !save.isPending ? <span className="field__hint">{t('catalog.saved')}</span> : null}
        </div>
      </form>
    </section>
  );
}

/**
 * Which doctors do this service (FR-201, CR-21).
 *
 * Nobody ticked means any doctor, which is the right default: most services at most clinics are
 * not restricted. Once somebody is ticked, booking refuses everyone else, with no override at the
 * desk, so the hint says so before the save rather than after.
 */
function DoctorsSection({
  clinicId,
  service,
  canManage,
}: {
  readonly clinicId: string;
  readonly service: ServiceResponse;
  readonly canManage: boolean;
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string[] | null>(null);

  const staff = useApiQuery<StaffMemberResponse[]>(queryKeys.staff(clinicId), `/clinics/${clinicId}/staff`);

  const save = useApiMutation<ServiceResponse, EligibleDoctorsRequest>(
    (body) => ({
      path: `/clinics/${clinicId}/catalog/services/${service.id}/doctors`,
      options: { method: 'PUT', body },
    }),
    { invalidates: [queryKeys.services(clinicId)], onSuccess: () => setSelected(null) },
  );

  const chosen = selected ?? (service.doctorEmploymentIds ?? []).filter((id): id is string => Boolean(id));

  return (
    <section className="section" aria-labelledby="service-doctors-heading">
      <h2 className="section__title" id="service-doctors-heading">
        {t('catalog.doctorsTitle')}
      </h2>
      <p className="field__hint" style={{ marginBlockEnd: 'var(--space-3)' }}>
        {t('catalog.doctorsHint')}
      </p>

      <AsyncSection query={staff}>
        {(rows) => {
          const doctors = rows.filter(
            (member) => member.role === 'DOCTOR' && member.employmentStatus === 'ACTIVE' && member.id,
          );
          if (doctors.length === 0) {
            return <p className="field__hint">{t('catalog.noDoctors')}</p>;
          }
          return (
            <ul style={{ listStyle: 'none', paddingInlineStart: 0 }}>
              {doctors.map((doctor) => (
                <li key={doctor.id}>
                  <label className="field">
                    <span className="field__label">
                      <input
                        type="checkbox"
                        checked={chosen.includes(doctor.id ?? '')}
                        disabled={!canManage}
                        onChange={(event) =>
                          setSelected(
                            event.target.checked
                              ? [...chosen, doctor.id ?? '']
                              : chosen.filter((id) => id !== doctor.id),
                          )
                        }
                      />{' '}
                      {doctor.displayName}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          );
        }}
      </AsyncSection>

      {chosen.length === 0 ? <p className="field__hint">{t('catalog.everyDoctor')}</p> : null}
      {save.error ? <ErrorState error={save.error} /> : null}

      {canManage ? (
        <div className="toolbar" style={{ marginBlockStart: 'var(--space-3)' }}>
          <button
            type="button"
            className="button button--primary"
            disabled={save.isPending || selected === null}
            onClick={() => save.mutate({ employmentIds: chosen })}
          >
            {save.isPending ? t('catalog.saving') : t('catalog.saveDoctors')}
          </button>
        </div>
      ) : null}
    </section>
  );
}

function StatusSection({
  clinicId,
  service,
  canManage,
}: {
  readonly clinicId: string;
  readonly service: ServiceResponse;
  readonly canManage: boolean;
}) {
  const { t } = useTranslation();
  const priced = isPriced(service);
  const needsPriceHint = service.pricingMode === 'PULSE' ? t('catalog.needsPulsePrice') : t('catalog.needsPrice');

  const changeStatus = useApiMutation<ServiceResponse, ChangeServiceStatusRequest>(
    (body) => ({
      path: `/clinics/${clinicId}/catalog/services/${service.id}/status`,
      options: { method: 'PUT', body },
    }),
    { invalidates: [queryKeys.services(clinicId)] },
  );

  const next: ServiceStatus = service.status === 'ACTIVE' ? 'ARCHIVED' : 'ACTIVE';

  return (
    <section className="section" aria-labelledby="service-status-heading">
      <h2 className="section__title" id="service-status-heading">
        {t('catalog.statusTitle')}
      </h2>
      <p className="field__hint">{priced ? t('catalog.statusHint') : needsPriceHint}</p>

      {canManage ? (
        <div className="toolbar" style={{ marginBlockStart: 'var(--space-3)' }}>
          <button
            type="button"
            className="button button--primary"
            disabled={changeStatus.isPending || (next === 'ACTIVE' && !priced)}
            onClick={() => changeStatus.mutate({ status: next })}
          >
            {next === 'ACTIVE' ? t('catalog.putOnSale') : t('catalog.withdraw')}
          </button>
        </div>
      ) : null}

      {changeStatus.error ? <ErrorState error={changeStatus.error} /> : null}
    </section>
  );
}

function AreasSection({
  clinicId,
  serviceId,
  pricedByPulse,
  canManage,
}: {
  readonly clinicId: string;
  readonly serviceId: string;
  readonly pricedByPulse: boolean;
  readonly canManage: boolean;
}) {
  const localised = useLocalisedName();
  const { t } = useTranslation();
  const [editingId, setEditingId] = useState<string | null>(null);

  const areas = useApiQuery<ServiceAreaResponse[]>(
    queryKeys.serviceAreas(clinicId, serviceId),
    `/clinics/${clinicId}/catalog/services/${serviceId}/areas`,
  );

  const addArea = useApiMutation<ServiceAreaResponse, CreateAreaRequest>(
    (body) => ({
      path: `/clinics/${clinicId}/catalog/services/${serviceId}/areas`,
      options: { method: 'POST', body },
    }),
    { invalidates: [queryKeys.serviceAreas(clinicId, serviceId), queryKeys.services(clinicId)] },
  );

  return (
    <section className="section" aria-labelledby="areas-heading">
      <h2 className="section__title" id="areas-heading">
        {t('catalog.areasTitle')}
      </h2>
      <p className="field__hint" style={{ marginBlockEnd: 'var(--space-3)' }}>
        {t('catalog.areasHint')}
      </p>
      {pricedByPulse ? (
        <p className="field__hint" style={{ marginBlockEnd: 'var(--space-3)' }}>
          {t('catalog.areasPricedByPulse')}
        </p>
      ) : null}

      <AsyncSection
        query={areas}
        isEmpty={(rows) => rows.length === 0}
        emptyTitle={t('catalog.noAreas')}
        emptyDescription={t('catalog.noAreasDescription')}
      >
        {(rows) => (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('catalog.code')}</th>
                  <th scope="col">{t('catalog.name')}</th>
                  <th scope="col">{t('catalog.duration')}</th>
                  <th scope="col">{t('catalog.pulsePreset')}</th>
                  <th scope="col">{t('catalog.price')}</th>
                  {canManage ? <th scope="col">{t('common.actions')}</th> : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((area) =>
                  editingId === area.id ? (
                    <AreaEditRow
                      key={area.id}
                      clinicId={clinicId}
                      serviceId={serviceId}
                      area={area}
                      onDone={() => setEditingId(null)}
                    />
                  ) : (
                    <tr key={area.id}>
                      <th scope="row" className="ltr">
                        {area.code}
                      </th>
                      <td>{localised.name(area)}</td>
                      <td className="numeric">{t('catalog.minutes', { n: area.durationMinutes ?? 0 })}</td>
                      <td className="numeric">{area.pulsePreset ?? 0}</td>
                      <td className="numeric">{money(area.price) ?? t('catalog.usesBasePrice')}</td>
                      {canManage ? (
                        <td>
                          <button
                            type="button"
                            className="button button--ghost"
                            onClick={() => setEditingId(area.id ?? null)}
                          >
                            {t('common.edit')}
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        )}
      </AsyncSection>

      {canManage ? (
        <form
          className="toolbar"
          style={{ marginBlockStart: 'var(--space-4)' }}
          onSubmit={(event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            addArea.mutate({
              code: String(form.get('code') ?? '').trim(),
              nameAr: String(form.get('nameAr') ?? '').trim() || undefined,
              durationMinutes: Number(form.get('durationMinutes') ?? 15),
              pulsePreset: Number(form.get('pulsePreset') ?? 0),
              price: readMoney(form.get('price')),
              displayOrder: 0,
            });
            event.currentTarget.reset();
          }}
        >
          <Field name="code" label={t('catalog.code')} required dir="ltr" />
          <Field name="nameAr" label={t('catalog.name')} />
          <label className="field">
            <span className="field__label">{t('catalog.duration')}</span>
            <input type="number" name="durationMinutes" className="field__input numeric" min={5} defaultValue={15} />
          </label>
          <label className="field">
            <span className="field__label">{t('catalog.pulsePreset')}</span>
            <input type="number" name="pulsePreset" className="field__input numeric" min={0} defaultValue={0} />
          </label>
          <MoneyField name="price" label={t('catalog.price')} />
          <button type="submit" className="button button--ghost" disabled={addArea.isPending}>
            {t('catalog.addArea')}
          </button>
        </form>
      ) : null}

      {addArea.error ? <ErrorState error={addArea.error} /> : null}
    </section>
  );
}

/**
 * One body area, being edited in place (CR-25): its length, reference pulses and price. Until CR-25
 * an area could only be added, so the starter pack's areas stayed unpriced and kept our pulse
 * estimates. The API replaces the details as a whole, so the names, sort order and whether the area
 * is in use, which this row does not edit, are sent back as they were. A blank price clears the
 * area's own price, and the service's base price is charged for it instead.
 */
function AreaEditRow({
  clinicId,
  serviceId,
  area,
  onDone,
}: {
  readonly clinicId: string;
  readonly serviceId: string;
  readonly area: ServiceAreaResponse;
  readonly onDone: () => void;
}) {
  const localised = useLocalisedName();
  const { t } = useTranslation();
  const [durationMinutes, setDurationMinutes] = useState(String(area.durationMinutes ?? 15));
  const [pulsePreset, setPulsePreset] = useState(String(area.pulsePreset ?? 0));
  const [price, setPrice] = useState(area.price?.amount ?? '');

  const save = useApiMutation<ServiceAreaResponse, UpdateAreaRequest>(
    (body) => ({ path: `/clinics/${clinicId}/catalog/areas/${area.id}`, options: { method: 'PATCH', body } }),
    { invalidates: [queryKeys.serviceAreas(clinicId, serviceId), queryKeys.services(clinicId)], onSuccess: onDone },
  );

  return (
    <tr>
      <th scope="row" className="ltr">
        {area.code}
      </th>
      <td>{localised.name(area)}</td>
      <td>
        <input
          type="number"
          className="field__input numeric"
          aria-label={`${t('catalog.duration')} ${area.code ?? ''}`}
          min={5}
          step={5}
          value={durationMinutes}
          onChange={(event) => setDurationMinutes(event.target.value)}
        />
      </td>
      <td>
        <input
          type="number"
          className="field__input numeric"
          aria-label={`${t('catalog.pulsePreset')} ${area.code ?? ''}`}
          min={0}
          value={pulsePreset}
          onChange={(event) => setPulsePreset(event.target.value)}
        />
      </td>
      <td>
        <input
          type="number"
          className="field__input numeric"
          aria-label={`${t('catalog.price')} ${area.code ?? ''}`}
          min={0}
          step="0.01"
          inputMode="decimal"
          placeholder={t('catalog.usesBasePrice')}
          value={price}
          onChange={(event) => setPrice(event.target.value)}
        />
        <span className="field__hint">{t('catalog.areaPriceHint')}</span>
        {save.error ? <ErrorState error={save.error} /> : null}
      </td>
      <td>
        <div className="toolbar">
          <button
            type="button"
            className="button button--primary"
            disabled={save.isPending || !(Number(durationMinutes) > 0)}
            onClick={() =>
              save.mutate({
                nameEn: area.nameEn,
                nameAr: area.nameAr,
                durationMinutes: Number(durationMinutes),
                pulsePreset: Number(pulsePreset || 0),
                price: readMoney(price),
                displayOrder: area.displayOrder ?? 0,
                active: area.active ?? true,
              })
            }
          >
            {save.isPending ? t('catalog.saving') : t('catalog.saveArea')}
          </button>
          <button type="button" className="button button--ghost" onClick={onDone}>
            {t('common.cancel')}
          </button>
        </div>
      </td>
    </tr>
  );
}

function BranchAvailabilitySection({
  clinicId,
  service,
  canManage,
}: {
  readonly clinicId: string;
  readonly service: ServiceResponse;
  readonly canManage: boolean;
}) {
  const localised = useLocalisedName();
  const { t } = useTranslation();
  const [everywhere, setEverywhere] = useState<boolean | null>(null);
  const [selected, setSelected] = useState<string[] | null>(null);

  const branches = useApiQuery<BranchResponse[]>(queryKeys.branches(clinicId), `/clinics/${clinicId}/branches`);

  const save = useApiMutation<ServiceResponse, BranchAvailabilityRequest>(
    (body) => ({
      path: `/clinics/${clinicId}/catalog/services/${service.id}/branches`,
      options: { method: 'PUT', body },
    }),
    {
      invalidates: [queryKeys.services(clinicId)],
      onSuccess: () => {
        setEverywhere(null);
        setSelected(null);
      },
    },
  );

  const offeredEverywhere = everywhere ?? service.offeredAtAllBranches ?? true;
  const chosen = selected ?? (service.branchIds ?? []).filter((id): id is string => Boolean(id));

  return (
    <section className="section" aria-labelledby="availability-heading">
      <h2 className="section__title" id="availability-heading">
        {t('catalog.availabilityTitle')}
      </h2>
      <p className="field__hint" style={{ marginBlockEnd: 'var(--space-3)' }}>
        {t('catalog.availabilityHint')}
      </p>

      <label className="field">
        <span className="field__label">
          <input
            type="checkbox"
            checked={offeredEverywhere}
            disabled={!canManage}
            onChange={(event) => setEverywhere(event.target.checked)}
          />{' '}
          {t('catalog.offeredEverywhere')}
        </span>
      </label>

      {!offeredEverywhere ? (
        <AsyncSection query={branches}>
          {(rows) => (
            <ul style={{ listStyle: 'none', paddingInlineStart: 0 }}>
              {rows.map((branch) => (
                <li key={branch.id}>
                  <label className="field">
                    <span className="field__label">
                      <input
                        type="checkbox"
                        checked={chosen.includes(branch.id ?? '')}
                        disabled={!canManage}
                        onChange={(event) =>
                          setSelected(
                            event.target.checked
                              ? [...chosen, branch.id ?? '']
                              : chosen.filter((id) => id !== branch.id),
                          )
                        }
                      />{' '}
                      {localised.name(branch, branch.code)}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </AsyncSection>
      ) : null}

      {save.error ? <ErrorState error={save.error} /> : null}

      {canManage ? (
        <div className="toolbar" style={{ marginBlockStart: 'var(--space-3)' }}>
          <button
            type="button"
            className="button button--primary"
            disabled={save.isPending || (everywhere === null && selected === null)}
            onClick={() => save.mutate({ everywhere: offeredEverywhere, branchIds: chosen })}
          >
            {save.isPending ? t('catalog.saving') : t('catalog.saveAvailability')}
          </button>
        </div>
      ) : null}
    </section>
  );
}
